import { useUserId, useUserInfo, useUserTeamId } from '@/hook/use-account'
import type { LinkProps } from '@/lib/link'
import { formatDate } from '@/lib/utils'
import {
  getFabricConversationFabricList,
  getFabricConversationUserList,
  postFabricConversationUserInfo,
  postFabricMessagePageList,
  postFabricMessageRead,
  type FabricConversationResponse,
  type FabricMessageResponse,
  type FabricInfoListResponse,
  type ProductInfo,
  postFabricMessageReadConversation,
  type FabricProductAssociation,
} from '@/service/fabric-api'
import type { AccessoryInfo } from '@/service/accessory-api'
import { create } from 'zustand'

type ChatUserInfo = ReturnType<typeof useUserInfo>
type GuessFabric = FabricInfoListResponse & { consultationTeamId?: number }
type GuessAccessory = AccessoryInfo & { consultationTeamId?: number }

export enum InquirySourceType {
  Goods = 1,
  Fabric = 2,
  Marchant = 3,
  Smart = 4,
}

export enum ChatFromType {
  Goods = 1,
  Fabric = 2,
  Merchant = 3,
  Smart = 4,
}

export enum LongLinkMessageTypes {
  // 未读
  NOREAD = 1,
  // 对方已读
  READ = 2,
  // 对方提交消息
  SUBMIT = 3,
  // 已读会话
  CONVERSATION = 4,
}

export enum MessageTypes {
  // 时间
  TIME = 0,
  // 文本
  TEXT = 1,
  // 图片
  IMAGE = 2,
  // 面料
  FABRIC = 3,
  // 商品
  PRODUCT = 4,
  // 面料案例
  FABRIC_CASE = 5,
  // 联系方式
  CONTACT = 6,
  // // 系统消息
  // SYSTEM = 7,
  // 找布需求
  FIND_DEMAND = 7,
  // 找布需求分配结果
  FIND_DEMAND_ASSIGNMENT = 8,
  // 辅料
  ACCESSORY = 9,
}

/** 测试代码 ： 找布需求卡片消息的兼容编码前缀：用于后端暂不支持 FIND_DEMAND 类型时，仍可通过 TEXT 发送并在前端解码展示 */
export const FIND_DEMAND_TEXT_PREFIX = '__FIND_DEMAND__:' as const

interface BaseMessage extends FabricMessageResponse {
  isMe: boolean
  type: MessageTypes
  contactSubmitted?: boolean
}

export interface TimeMessage extends BaseMessage {
  type: MessageTypes.TIME
}

export interface TextMessage extends BaseMessage {
  type: MessageTypes.TEXT
}

export interface ImageMessage extends BaseMessage {
  type: MessageTypes.IMAGE
}

export interface FabricMessage extends BaseMessage {
  type: MessageTypes.FABRIC
  message?: string
  data: {
    fabricId: number
    fabricDetailPic: string
    fabricName: string
    teamId: number
    teamName: string
  }
}

export interface ProductMessage extends BaseMessage {
  type: MessageTypes.PRODUCT
  message?: string
  data: ProductInfo & LinkProps
}

export interface FabricCaseMessage extends BaseMessage {
  type: MessageTypes.FABRIC_CASE
  data: {
    fabricId: string
    fabricName: string
    fabricCasePic: string
  }
}

export interface ContactMessage extends BaseMessage {
  type: MessageTypes.CONTACT
  isFill: boolean
  data: {
    phone?: string
    wechat?: string
    qrcode?: string
  }
}

function getContactShotMessage(content?: string | null) {
  try {
    const parsedContent = content ? JSON.parse(content) : {}
    return parsedContent.isFill ? '[客户已提交联系方式]' : '[邀请留下联系方式]'
  } catch {
    return '[邀请留下联系方式]'
  }
}

export interface FindDemandAssignmentMessage extends BaseMessage {
  type: MessageTypes.FIND_DEMAND_ASSIGNMENT
  /**系统提示 */
  data: {
    message?: string
    data: {
      /**id */
      assignFabricId: string[]
      assignFabricList: {
        category: string
        detailUrls: string[]
        fabric: string
        fabricId: string
        fabricName: string
        garmentDisplayPicList: string[]
        mainUrl: string
        price: string
        priceMax: string
        priceMin: string
        priceUnit: string
        sellerId: string
      }[]
      /** 分配上传的面料图片 */
      assignFabricPicList: string[]
      /**补充说明 */
      assignRemark: string
      /** 分配商家ID列表 */
      assignTeamId: string[]
      /** 分配商家名称列表 */
      assignTeamList: {
        contactPerson: string
        contactPhone: string
        sellerLogo: string
        sellerName: string
        primaryProducts: string
      }[]
      assignTeamName: string[]
      createdAt: string
      phone: string
      referenceDesc: string
      referencePicList: string[]
      requirementId: string
      sourceType: number
      status: number
      teamId: number
      teamName: string
      userId: number
      userName: string
      wechat: string
    }
  }
}

export enum FindDemandStatus {
  SERVING = 'SERVING',
  PENDING_CONFIRM = 'PENDING_CONFIRM',
  FINISHED = 'FINISHED',
}

export enum FindDemandSubmitChannel {
  MINI_PROGRAM = 'MINI_PROGRAM',
  WEB = 'WEB',
  OFFICIAL = 'OFFICIAL',
}

export interface FindDemand {
  id: string
  createdAt: number
  status: FindDemandStatus
  submitChannel: FindDemandSubmitChannel
  desc: string
  images: string[]
  quantity?: string
  expectedPrice?: string
  spotRequirement?: string
  phone?: string
  wechat?: string
}

export interface FindDemandMessage extends BaseMessage {
  type: MessageTypes.FIND_DEMAND
  data: {
    demand: FindDemand
  }
}

export interface AccessoryMessage extends BaseMessage {
  type: MessageTypes.ACCESSORY
  message?: string
  data: AccessoryInfo
}

export type Message =
  | TimeMessage
  | TextMessage
  | ImageMessage
  | FabricMessage
  | ProductMessage
  | FabricCaseMessage
  | ContactMessage
  | FindDemandAssignmentMessage
  | FindDemandMessage
  | AccessoryMessage

function isOutgoingMessage(item: FabricMessageResponse, userId: number, userTeamId: number, type: 'marchant' | 'user') {
  return item.fromUserId === userId || (type === 'marchant' && item.fromTeamId === userTeamId && !item.isSystem)
}

function shouldHideSystemMessage(item: Pick<FabricMessageResponse, 'isSystem'>, type: 'marchant' | 'user') {
  return type === 'user' && !!item.isSystem
}

function isUnreadIncomingMessage(item: Pick<FabricMessageResponse, 'isRead' | 'isSystem'> & { isMe?: boolean }) {
  return !item.isRead && !item.isMe && !item.isSystem
}

function sortMessages(messages: Message[]) {
  return [...messages].sort((a, b) => {
    const sendTimeDiff = (b.sendTime || 0) - (a.sendTime || 0)
    if (sendTimeDiff !== 0) return sendTimeDiff
    return (b.id || 0) - (a.id || 0)
  })
}

function mergeMessages(currentMessages: Message[], incomingMessages: Message[]) {
  const messageMap = new Map<number, Message>()
  currentMessages.forEach((item) => {
    if (item.id !== undefined) messageMap.set(item.id, item)
  })
  incomingMessages.forEach((item) => {
    if (item.id === undefined) return
    const current = messageMap.get(item.id)
    messageMap.set(
      item.id,
      current
        ? {
            ...item,
            // 已经由实时链路标记的状态不能被稍早的历史接口响应覆盖。
            isRead: current.isRead || item.isRead,
            contactSubmitted: current.contactSubmitted || item.contactSubmitted,
          }
        : item,
    )
  })
  return sortMessages(Array.from(messageMap.values()))
}

function rememberMessageId(messageIds: Set<number>, id?: number) {
  if (id === undefined) return messageIds
  const nextMessageIds = new Set(messageIds)
  nextMessageIds.add(id)
  // 页面长时间打开时限制去重缓存，避免消息量无限增长。
  while (nextMessageIds.size > 1000) {
    const oldestId = nextMessageIds.values().next().value
    if (oldestId === undefined) break
    nextMessageIds.delete(oldestId)
  }
  return nextMessageIds
}

function rememberMessageIds(messageIds: Set<number>, messages: Message[]) {
  return messages.reduce((ids, message) => rememberMessageId(ids, message.id), messageIds)
}

function sortUserChats<T extends FabricConversationResponse & { showLatestMessage: string }>(chats: T[]) {
  return [...chats].sort((a, b) => {
    // 官方客服保持在列表顶部，其余会话按最新消息倒序排列。
    const customerDiff = (b.isCustomer || 0) - (a.isCustomer || 0)
    if (customerDiff !== 0) return customerDiff
    const aTime = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : 0
    const bTime = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : 0
    return bTime - aTime
  })
}

function mergeChatSnapshots(
  remoteChats: (FabricConversationResponse & { showLatestMessage: string })[],
  localChats: (FabricConversationResponse & { showLatestMessage: string })[],
) {
  const localMap = new Map(localChats.map((item) => [item.conversationId, item]))
  const remoteConversationIds = new Set(remoteChats.map((item) => item.conversationId))
  const mergedChats = remoteChats.map((remote) => {
    const local = localMap.get(remote.conversationId)
    if (!local) return remote
    const remoteTime = remote.lastMessageTime ? new Date(remote.lastMessageTime).getTime() : 0
    const localTime = local.lastMessageTime ? new Date(local.lastMessageTime).getTime() : 0
    if (localTime < remoteTime) return remote
    return {
      ...remote,
      showLatestMessage: localTime >= remoteTime ? local.showLatestMessage : remote.showLatestMessage,
      lastMessageTime: localTime >= remoteTime ? local.lastMessageTime : remote.lastMessageTime,
      countMessage: Math.max(remote.countMessage || 0, local.countMessage || 0),
    }
  })
  // 消息推送和会话列表接口存在短暂的落库窗口，保留本地已标记未读但尚未出现在远端列表的会话。
  return sortUserChats([
    ...mergedChats,
    ...localChats.filter((item) => !remoteConversationIds.has(item.conversationId) && (item.countMessage || 0) > 0),
  ])
}

function updateUserChatForMessage(
  chats: (FabricConversationResponse & { showLatestMessage: string })[],
  message: FabricMessageResponse,
  getShotMessage: (item: FabricMessageResponse) => string,
  incrementUnread: boolean,
) {
  return sortUserChats(
    chats.map((item) => {
      if (item.conversationId !== message.conversationId) return item
      return {
        ...item,
        showLatestMessage: getShotMessage(message),
        lastMessageTime: formatDate(new Date(message.sendTime || 0), 'YYYY-MM-DD HH:mm:ss'),
        countMessage: (item.countMessage || 0) + (incrementUnread ? 1 : 0),
      }
    }),
  )
}

// 轮询和 WebSocket 可能同时刷新列表或历史，只允许当前请求写回状态。
let userChatsRequestSequence = 0
let messageRequestSequence = 0

function applyContactSubmittedStatus(messages: Message[], messageIds?: number[]) {
  if (!messageIds?.length) return messages
  const messageIdSet = new Set(messageIds)
  return messages.map((item) => (item.id !== undefined && messageIdSet.has(item.id) ? { ...item, contactSubmitted: true } : item))
}

function getContactSubmittedMessageIds(message: FabricMessageResponse) {
  return (message as FabricMessageResponse & { contactSubmittedMessageIds?: number[] }).contactSubmittedMessageIds
}

interface ChatState {
  userInfo: ChatUserInfo
  userId: number
  getUserInfo: () => void
  userTeamId: number
  getUserTeamId: () => void
  callRule: boolean
  setCallRule: (value: boolean) => void
  superAdmin: boolean
  setSuperAdmin: (value: boolean) => void
  dnd: number
  setDnd: (value: number) => void
  type: 'marchant' | 'user'
  setType: (type: 'marchant' | 'user') => void
  loading: boolean
  noReadTotal: number
  setNoReadTotal: (num: number) => void
  pageNo: number
  pageSize: number
  total: number
  messages: Message[]
  receivedMessageIds: Set<number>
  getMessage: (conversationId: string) => Promise<void>
  reloadMessage: (conversationId: string) => Promise<void>
  nextMessage: () => void
  updateMessage: (messageId: number[], data: object) => void
  sendMessage: (conversationId: string, message: FabricMessageResponse) => void
  acceptMessage: (message: FabricMessageResponse, isAtBottom?: boolean) => void
  readMessage: (messageIds: number[]) => void
  readAllUnreadMessage: () => void
  readMyAllMessage: () => void
  filterMessage: (item: FabricMessageResponse) => Message
  getShotMessage: (item: FabricMessageResponse) => string
  updateUnread: () => void
  merchantView: boolean
  setMerchantView: (value?: boolean) => void
  recentlyView: boolean
  setRecentlyView: (value?: boolean) => void
  chats: (FabricConversationResponse & { showLatestMessage: string })[]
  getChats: () => Promise<void>
  getUserChats: () => Promise<void>
  targetChatUserId: number | null
  setTargetChatUserId: (userId?: number) => void
  targetChatFabricTeamId: number | null
  setTargetChatFabricTeamId: (fabricTeamId?: number) => void
  defaultMessage: string
  setDefaultMessage: (value: string) => void
  currentChat: FabricConversationResponse | null
  setCurrentChat: (chat: FabricConversationResponse | null, autoRead?: boolean) => void
  readConversation: (conversationId: string, fromTeamId: number, fromUserId: number) => void
  getCustomerInfo: (conversationId: string) => void
  customerInfo: FabricConversationResponse
  setCustomerInfo: (info: FabricConversationResponse) => void
  guessFabric: GuessFabric | null
  setGuessFabric: (goods: GuessFabric | null) => void
  guessAccessory: GuessAccessory | null
  setGuessAccessory: (accessory: GuessAccessory | null) => void
  guessGoods: (Omit<FabricProductAssociation, 'sourceChannel'> & { sourceChannel?: number; submitPageUrl?: string }) | null
  setGuessGoods: (
    goods: (Omit<FabricProductAssociation, 'sourceChannel'> & { sourceChannel?: number; submitPageUrl?: string }) | null,
  ) => void
  isAtBottom: boolean
  setIsAtBottom: (value: boolean) => void
  isIframeVisible: boolean
  setIsIframeVisible: (value: boolean) => void
}

export const useChatStore = create<ChatState>((set, get) => ({
  // 当前登录用户的基本信息
  userInfo: {} as ChatUserInfo,
  userId: 0,
  // 获取并保存当前登录用户的基本信息
  getUserInfo: () => {
    set(() => ({
      userInfo: useUserInfo(),
      userId: useUserId(),
    }))
  },
  userTeamId: 0,
  getUserTeamId: () => {
    set(() => ({
      userTeamId: useUserTeamId(),
    }))
  },
  // 是否有打招呼权限
  callRule: false,
  // 设置是否有打招呼权限
  setCallRule: (value: boolean) => {
    set(() => ({ callRule: value }))
  },
  // 是否超级管理员
  superAdmin: false,
  // 设置是否为超级管理员
  setSuperAdmin(value: boolean) {
    set(() => ({ superAdmin: value }))
  },
  // 是否免打扰 0:否 1:是
  dnd: 0,
  // 设置是否免打扰
  setDnd: (value: number) => {
    set(() => ({ dnd: value }))
  },
  // 商家端：marchant 用户端：user
  type: 'marchant' as const,
  // 设置当前为商家端还是用户端
  setType: (type: 'marchant' | 'user') => {
    set(() => ({ type }))
  },
  // 用户端是否打开商家详情
  merchantView: true,
  // 设置用户端是否打开商家详情
  setMerchantView: (value?: boolean) => {
    set((state) => ({ merchantView: value === undefined ? !state.merchantView : value }))
  },
  // 用户端是否打开最近查看
  recentlyView: false,
  // 用户端控制是否打开最近查看
  setRecentlyView: (value?: boolean) => {
    set((state) => ({ recentlyView: value === undefined ? !state.recentlyView : value }))
  },
  // 控制是否正在获取消息数据
  loading: false,
  // 当前总共未读消息数量
  noReadTotal: 0,
  // 设置当前总共未读消息数量
  setNoReadTotal: (num: number) => {
    set(() => ({
      noReadTotal: num,
    }))
  },
  // 消息列表页码
  pageNo: 1,
  // 消息列表每页数量
  pageSize: 10,
  // 当前会话总共的消息数量
  total: 0,
  // 消息列表
  messages: [],
  receivedMessageIds: new Set(),
  // 获取消息列表
  getMessage: async (conversationId: string) => {
    if (!conversationId) return
    const requestSequence = messageRequestSequence
    set(() => ({ loading: true }))
    try {
      const res = await postFabricMessagePageList({
        conversationId,
        pageNo: get().pageNo,
        pageSize: get().pageSize,
      })
      if (requestSequence !== messageRequestSequence || conversationId !== get().currentChat?.conversationId) return
      set((state) => {
        const newRes = (res.resultList || []).filter((item) => !shouldHideSystemMessage(item, state.type))
        const incoming = newRes.map((item) => state.filterMessage(item))
        return {
          total: res.resultCount || 0,
          messages: mergeMessages(state.messages, incoming),
          receivedMessageIds: rememberMessageIds(state.receivedMessageIds, incoming),
        }
      })
    } finally {
      if (requestSequence === messageRequestSequence) {
        set(() => ({ loading: false }))
      }
    }
  },
  reloadMessage: async (conversationId: string) => {
    if (!conversationId) return
    const requestSequence = ++messageRequestSequence
    const stateBeforeRequest = get()
    const requestPageSize = Math.max(stateBeforeRequest.pageSize, stateBeforeRequest.messages.length || 0)
    // 保留实时链路已经展示的消息，避免补拉期间右侧短暂变空。
    set(() => ({ pageNo: 1, loading: true }))
    try {
      const res = await postFabricMessagePageList({
        conversationId,
        pageNo: 1,
        pageSize: requestPageSize,
      })
      if (requestSequence !== messageRequestSequence || conversationId !== get().currentChat?.conversationId) return
      set((state) => {
        const newRes = (res.resultList || []).filter((item) => !shouldHideSystemMessage(item, state.type))
        const incoming = newRes.map((item) => state.filterMessage(item))
        return {
          total: res.resultCount || 0,
          messages: mergeMessages(state.messages, incoming),
          receivedMessageIds: rememberMessageIds(state.receivedMessageIds, incoming),
        }
      })
    } finally {
      if (requestSequence === messageRequestSequence) {
        set(() => ({ loading: false }))
      }
    }
  },
  // 获取下一页消息
  nextMessage: () => {
    set((state) => ({ pageNo: state.pageNo + 1 }))
    get().getMessage(get().currentChat?.conversationId || '')
  },
  // 根据消息id更新消息数据
  updateMessage: (messageId: number[], data: object) => {
    set((state) => {
      return {
        messages: state.messages.map((item) => {
          if (messageId.includes(item.id!)) {
            return {
              ...item,
              ...data,
            }
          } else {
            return item
          }
        }),
      }
    })
    get().updateUnread()
  },
  // 发送消息
  sendMessage: (conversationId: string, message: FabricMessageResponse) => {
    // 如果发送的消息目标不是当前会话，则直接返回，防止发送过程中切换会话导致消息展示错误
    if (conversationId !== get().currentChat?.conversationId) return
    set((state) => {
      const filteredMessage = state.filterMessage(message)
      const messages = state.messages.some((item) => item.id === filteredMessage.id)
        ? applyContactSubmittedStatus(state.messages, getContactSubmittedMessageIds(message))
        : [filteredMessage, ...applyContactSubmittedStatus(state.messages, getContactSubmittedMessageIds(message))]
      const chats = updateUserChatForMessage(state.chats, message, state.getShotMessage, false)
      const currentChat = state.currentChat
        ? {
            ...state.currentChat,
            ...chats.find((item) => item.conversationId === state.currentChat?.conversationId),
          }
        : state.currentChat
      return {
        messages,
        chats,
        currentChat,
        receivedMessageIds: rememberMessageId(state.receivedMessageIds, message.id),
      }
    })
  },
  acceptMessage: (message: FabricMessageResponse, isAtBottom?: boolean) => {
    // console.log('接收到消息：', message)
    if (shouldHideSystemMessage(message, get().type)) {
      return
    }
    const filteredMessage = get().filterMessage(message)
    const applySubmittedStatus = (messages: Message[]) =>
      applyContactSubmittedStatus(messages, getContactSubmittedMessageIds(message))
    const isOutgoing = filteredMessage.isMe
    const state = get()
    const isCurrentChat = message.conversationId === state.currentChat?.conversationId
    const isVisibleAtBottom = isCurrentChat && state.isIframeVisible && !!isAtBottom
    const isUnreadIncoming = !isOutgoing && !message.isSystem
    const isDuplicateMessage = message.id !== undefined && state.receivedMessageIds.has(message.id)
    const shouldIncrementUnread = isUnreadIncoming && !isVisibleAtBottom && !isDuplicateMessage
    const exists = isCurrentChat && state.messages.some((item) => item.id === filteredMessage.id)
    const messages = isCurrentChat
      ? exists
        ? applySubmittedStatus(state.messages)
        : [
            { ...filteredMessage, isRead: isVisibleAtBottom && isUnreadIncoming ? 1 : filteredMessage.isRead },
            ...applySubmittedStatus(state.messages),
          ]
      : state.messages
    const chats = updateUserChatForMessage(state.chats, message, state.getShotMessage, shouldIncrementUnread)
    const currentChat = state.currentChat
      ? {
          ...state.currentChat,
          ...chats.find((item) => item.conversationId === state.currentChat?.conversationId),
        }
      : state.currentChat

    if (isCurrentChat && isVisibleAtBottom && isUnreadIncoming && !exists) {
      postFabricMessageRead([filteredMessage.id])
    }
    if (message.isSystem && isCurrentChat) {
      get().getCustomerInfo(message.conversationId!)
    }
    set(() => ({
      messages,
      chats,
      currentChat,
      noReadTotal: chats.reduce((total, item) => total + (item.countMessage || 0), 0),
      receivedMessageIds: rememberMessageId(state.receivedMessageIds, message.id),
    }))
  },
  // 已读消息
  readMessage: (messageIds: number[]) => {
    // 找到存在于会话列表中并且未读的消息id
    const readItems = messageIds.filter((item) =>
      get().messages.find((message) => message.id === item && isUnreadIncomingMessage(message)),
    )
    // 存在未读消息，调用接口进行已读操作
    // console.log('已读消息：', readItems)
    if (readItems.length > 0) {
      postFabricMessageRead(readItems)
    }
    // 将消息设置为已读状态
    get().updateMessage(readItems, { isRead: 1 })
    // 更新相关数据
    get().updateUnread()
    // console.log(
    //   '已读完成后未读数量：',
    //   get().noReadTotal,
    //   get().chats.filter((item) => item.countMessage),
    // )
  },
  // 已读当前会话所有未读的消息
  readAllUnreadMessage: () => {
    const unreadMessageIds = get()
      .messages.filter((item) => isUnreadIncomingMessage(item))
      .map((item) => item.id!)
    get().readMessage(unreadMessageIds)
  },
  // 已读当前会话所有我发送的未读消息
  readMyAllMessage: () => {
    const unreadMessageIds = get()
      .messages.filter((item) => item.isMe && !item.isRead)
      .map((item) => item.id!)
    get().updateMessage(unreadMessageIds, { isRead: 1 })
  },
  // 根据消息类型进行格式化
  filterMessage: (item: FabricMessageResponse) => {
    if (!get().userId) {
      get().getUserInfo()
    }
    const isMe = isOutgoingMessage(item, get().userId, get().userTeamId, get().type)
    // 兼容：后端暂不支持 FIND_DEMAND 类型时，用 TEXT + 前缀编码发送，前端在此处解码成 FIND_DEMAND 展示
    if (typeof item.content === 'string' && item.content.startsWith(FIND_DEMAND_TEXT_PREFIX)) {
      const parsedContent = JSON.parse(item.content.slice(FIND_DEMAND_TEXT_PREFIX.length))
      return {
        ...item,
        type: MessageTypes.FIND_DEMAND,
        data: parsedContent,
        isMe: item.fromUserId === get().userId,
      } as FindDemandMessage
    }
    if (!item.type) {
      return {
        ...item,
        message: item.content!,
        isMe,
      } as Message
    }
    if (item.type === MessageTypes.FABRIC) {
      const parsedContent = JSON.parse(item.content!)
      return {
        ...item,
        type: item.type,
        data: parsedContent.data || {},
        message: parsedContent.message || '',
        isMe,
      } as FabricMessage
    } else if (item.type === MessageTypes.PRODUCT) {
      const parsedContent = JSON.parse(item.content!)
      return {
        ...item,
        type: item.type,
        message: parsedContent.message || '',
        data: parsedContent.data || {},
        isMe,
      } as ProductMessage
    } else if (item.type === MessageTypes.FABRIC_CASE) {
      const parsedContent = JSON.parse(item.content!)
      return {
        ...item,
        type: item.type,
        data: parsedContent.data || {},
        isMe,
      } as FabricCaseMessage
    } else if (item.type === MessageTypes.CONTACT) {
      const parsedContent = JSON.parse(item.content!)
      return {
        ...item,
        type: item.type,
        isFill: parsedContent.isFill,
        data: parsedContent.data || {},
        isMe,
      } as ContactMessage
    } else if (item.type === MessageTypes.FIND_DEMAND_ASSIGNMENT) {
      const parsedContent = item.content ? JSON.parse(item.content) : {}
      return {
        ...item,
        type: item.type,
        data: parsedContent,
        isMe,
      } as FindDemandAssignmentMessage
    } else if (item.type === MessageTypes.FIND_DEMAND) {
      const parsedContent = item.content ? JSON.parse(item.content) : {}
      return {
        ...item,
        type: item.type,
        data: parsedContent,
        isMe,
      } as FindDemandMessage
    } else if (item.type === MessageTypes.ACCESSORY) {
      const parsedContent = item.content ? JSON.parse(item.content) : {}
      return {
        ...item,
        type: item.type,
        data: parsedContent.data || {},
        message: parsedContent.message || '',
        isMe,
      } as AccessoryMessage
    }
    return {
      ...item,
      type: item.type,
      message: item.content!,
      isMe,
    } as TextMessage | ImageMessage
  },
  // 根据消息类型返回对应的缩写
  getShotMessage: (item: FabricMessageResponse) => {
    if (shouldHideSystemMessage(item, get().type)) {
      return ''
    }
    if (item.type === MessageTypes.TEXT && item.content?.startsWith(FIND_DEMAND_TEXT_PREFIX)) {
      return '[找布需求]'
    }
    if (item.type === MessageTypes.IMAGE) {
      return '[图片]'
    } else if (item.type === MessageTypes.FABRIC) {
      return '[面料]'
    } else if (item.type === MessageTypes.PRODUCT) {
      return '[商品]'
    } else if (item.type === MessageTypes.FABRIC_CASE) {
      return '[面料案例]'
    } else if (item.type === MessageTypes.CONTACT) {
      return getContactShotMessage(item.content)
    } else if (item.type === MessageTypes.FIND_DEMAND_ASSIGNMENT) {
      return '[找布需求推荐]'
    } else if (item.type === MessageTypes.FIND_DEMAND) {
      return '[找布需求]'
    } else if (item.type === MessageTypes.ACCESSORY) {
      return '[辅料]'
    }
    return item.content || ''
  },
  // 更新消息列表、会话列表、总未读数量
  updateUnread() {
    // console.log('更新未读信息')
    set((state) => {
      // 获取当前会话的未读数量
      const currentUnread = state.messages.filter(
        (item) => isUnreadIncomingMessage(item) && item.conversationId === state.currentChat?.conversationId,
      )
      // 更新会话列表的未读数量
      const currentChats = state.chats.map((item) => {
        if (item.conversationId === state.currentChat?.conversationId) {
          return {
            ...item,
            countMessage: currentUnread.length,
          }
        } else {
          return item
        }
      })
      // 更新当前会话的未读数量
      const currentChat = state.currentChat
        ? {
            ...state.currentChat,
            countMessage: currentUnread.length,
          }
        : null
      // 统计总共的未读数量
      const noReadTotal = currentChats.reduce((a, b) => a + (b.countMessage || 0), 0)
      return {
        messages: state.messages,
        chats: currentChats,
        currentChat,
        noReadTotal,
      }
    })
  },
  chats: [],
  // 获取商家端会话列表
  getChats: async () => {
    // console.log('获取商家端会话列表')
    const res = await getFabricConversationFabricList()
    // 更新会话列表和总未读数量
    set(() => ({
      chats: res.map((item) => {
        return {
          ...item,
          showLatestMessage: get().getShotMessage(item),
        }
      }),
      noReadTotal: res.reduce((a, b) => {
        return a + (b.countMessage || 0)
      }, 0),
    }))
    if (!res.length || (get().currentChat && !get().targetChatFabricTeamId && !get().targetChatUserId)) return
    // 如果不存在当前联系人，则进行更新，先判断是否有目标联系人，若没有则默认第一个
    let defaultChat = res[0]
    let flag = false
    if (get().targetChatUserId) {
      defaultChat = res.find((item) => item.userId === get().targetChatUserId) || res[0]
      flag = true
      set(() => ({ targetChatUserId: null }))
    }
    if (get().targetChatFabricTeamId) {
      defaultChat = res.find((item) => item.fabricTeamId === get().targetChatFabricTeamId) || res[0]
      flag = true
      set(() => ({ targetChatFabricTeamId: null }))
    }
    // 自动选择默认聊天时不自动标记为已读
    get().setCurrentChat(defaultChat, flag)
  },
  // 获取用户端会话列表
  getUserChats: async () => {
    // console.log('获取用户端会话列表')
    const requestSequence = ++userChatsRequestSequence
    const res = await getFabricConversationUserList()
    if (requestSequence !== userChatsRequestSequence) return
    const remoteChats = sortUserChats(
      res.map((item) => ({
        ...item,
        showLatestMessage: item.isSystem ? '' : get().getShotMessage(item),
      })),
    )
    // 列表接口可能早于消息落库返回，保留实时推送已经写入的最新预览和未读数。
    const chats = sortUserChats(mergeChatSnapshots(remoteChats, get().chats))
    const selectedChat = chats.find((item) => item.conversationId === get().currentChat?.conversationId)
    set((state) => ({
      chats,
      noReadTotal: chats.reduce((total, item) => total + (item.countMessage || 0), 0),
      currentChat: state.currentChat && selectedChat ? { ...state.currentChat, ...selectedChat } : state.currentChat,
    }))
    const isInChats = selectedChat
    if (!res.length || (get().currentChat && isInChats && !get().targetChatFabricTeamId && !get().targetChatUserId)) return
    // 如果不存在当前联系人，则进行更新，先判断是否有目标联系人，若没有则默认第一个
    let defaultChat = chats[0]
    let flag = false
    if (get().targetChatUserId) {
      defaultChat = chats.find((item) => item.userId === get().targetChatUserId) || chats[0]
      flag = true
      set(() => ({ targetChatUserId: null }))
    }
    if (get().targetChatFabricTeamId) {
      defaultChat = chats.find((item) => item.fabricTeamId === get().targetChatFabricTeamId) || chats[0]
      flag = true
      set(() => ({ targetChatFabricTeamId: null }))
    }
    if (defaultChat) {
      // 自动选择默认聊天时不自动标记为已读
      get().setCurrentChat(defaultChat, flag)
    }
  },
  // 目标聊天用户
  targetChatUserId: null,
  setTargetChatUserId: (userId?: number) => {
    set(() => ({ targetChatUserId: userId }))
  },
  // 目标聊天商家
  targetChatFabricTeamId: null,
  setTargetChatFabricTeamId: (fabricTeamId?: number) => {
    set(() => ({ targetChatFabricTeamId: fabricTeamId }))
  },
  defaultMessage: '',
  setDefaultMessage: (value: string) => {
    set(() => ({ defaultMessage: value }))
  },
  // 当前联系人
  currentChat: null,
  setCurrentChat: (chat: FabricConversationResponse | null, autoRead?: boolean) => {
    if (!chat) {
      messageRequestSequence += 1
      return set(() => ({ currentChat: null, customer: {}, messages: [], pageNo: 1, total: 0 }))
    }
    if (chat.conversationId === get().currentChat?.conversationId) return
    messageRequestSequence += 1
    // console.log('选中会话：', chat, autoRead)
    set(() => ({ currentChat: chat, customer: {}, customerInfo: chat, messages: [], pageNo: 1, total: 0 }))
    get().getMessage(chat.conversationId!)
    get().getCustomerInfo(chat.conversationId!)
    // 只有在iframe可见时才自动标记为已读
    if (autoRead && (get().isIframeVisible || get().type === 'marchant')) {
      // console.log('自动已读会话')
      get().readConversation(
        chat.conversationId!,
        get().type === 'marchant' ? chat.teamId! : chat.fabricTeamId!,
        get().type === 'marchant' ? chat.userId! : chat.fabricUserId!,
      )
      // 更新本地状态，将当前会话的所有未读消息标记为已读
      set((state) => ({
        chats: state.chats.map((item) => {
          if (item.conversationId === chat.conversationId) {
            return {
              ...item,
              countMessage: 0,
            }
          } else {
            return item
          }
        }),
        noReadTotal: state.chats.reduce((a, b) => {
          return a + (b.conversationId === chat.conversationId ? 0 : b.countMessage || 0)
        }, 0),
      }))
      // console.log(
      //   '已读完成后未读数量：',
      //   get().noReadTotal,
      //   get().chats.filter((item) => item.countMessage),
      // )
    }
  },
  readConversation: (conversationId: string, fromTeamId: number, fromUserId: number) => {
    // console.log('已读当前会话框：', conversationId, fromTeamId)
    postFabricMessageReadConversation({
      fromTeamId: fromTeamId,
      fromUserId: fromUserId,
      conversationId: conversationId,
    })
  },
  // 获取当前联系人的信息
  getCustomerInfo: (conversationId: string) => {
    if (!conversationId) return
    postFabricConversationUserInfo({
      conversationId: conversationId,
    }).then((res) => {
      if (conversationId !== get().currentChat?.conversationId) return
      set(() => ({ customerInfo: res }))
    })
  },
  // 当前联系人的信息
  customerInfo: {},
  setCustomerInfo: (info: FabricConversationResponse) => {
    set(() => ({ customerInfo: info }))
  },
  // 大知衣发起咨询猜测要发送的面料
  guessFabric: null,
  setGuessFabric: (goods: GuessFabric | null) => {
    set(() => ({ guessFabric: goods }))
  },
  // 面料中心发起咨询时，等待用户确认发送的辅料
  guessAccessory: null,
  setGuessAccessory: (accessory: GuessAccessory | null) => {
    set(() => ({ guessAccessory: accessory }))
  },
  // 大知衣发起咨询猜测要发送的商品
  guessGoods: null,
  setGuessGoods: (
    goods: (Omit<FabricProductAssociation, 'sourceChannel'> & { sourceChannel?: number; submitPageUrl?: string }) | null,
  ) => {
    set(() => ({ guessGoods: goods }))
  },
  // 滚动条是否在底部
  isAtBottom: true,
  setIsAtBottom: (value: boolean) => {
    // console.log('滚动条是否在底部：', value)
    set(() => ({ isAtBottom: value }))
  },
  isIframeVisible: false,
  setIsIframeVisible: (value: boolean) => {
    // console.log('用户端会话窗口是否打开：', value)
    set(() => ({ isIframeVisible: value }))
  },
}))
